// src/modals/CreateInvoiceModal.jsx
import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import SearchBuildingBar from "../../../components/SearchBuildingBar";
import { useAuth } from "../../../providers/authprovider";

function handleResponseCode(status, fallbackError) {
  if (!status) return fallbackError || "Nieznany błąd.";

  switch (status) {
    case 400:
      return "Nieprawidłowe dane wejściowe.";
    case 401:
      return "Brak autoryzacji. Zaloguj się ponownie.";
    case 403:
      return "Brak uprawnień do wykonania tej operacji.";
    case 404:
      return "Nie znaleziono zasobu.";
    case 409:
      return "Konflikt danych. Rekord już istnieje.";
    case 413:
      return "Plik jest zbyt duży.";
    case 415:
      return "Nieobsługiwany format pliku.";
    case 422:
      return "Błąd walidacji danych.";
    case 429:
      return "Zbyt wiele żądań. Spróbuj ponownie później.";
    case 500:
      return "Błąd serwera. Spróbuj ponownie za chwilę.";
    case 502:
    case 503:
    case 504:
      return "Serwis chwilowo niedostępny.";
    default:
      return fallbackError || "Wystąpił nieoczekiwany błąd.";
  }
}

export default function CreateInvoiceModal({
  show,
  onHide,

  // show({...params}) może przekazać:
  title = "Nowa faktura",
  initialBuilding = null,

  // edit mode:
  // invoice: { id, description, file_name, ... }
  invoice = null,

  initialDescription = "",

  invoiceToIssueId = null,

  // faktura cykliczna: { id, ... } + domyślna kolejna data ("YYYY-MM-DD")
  recurringInvoiceId = null,
  initialNextInvoiceDate = "",
  onSaved, // (invoice, building, extra) => void; extra: { nextInvoiceDate }
}) {
  const { invoices } = useAuth();

  const isEdit = !!invoice?.id;
  const isRecurring = !!recurringInvoiceId;

  const [building, setBuilding] = useState(initialBuilding);
  const [description, setDescription] = useState(invoice?.description || initialDescription);
  const [nextInvoiceDate, setNextInvoiceDate] = useState(initialNextInvoiceDate);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // reset przy każdym otwarciu
  React.useEffect(() => {
    if (!show) return;


    setBuilding(initialBuilding || null);
    setDescription(invoice?.description || initialDescription || "");
    setNextInvoiceDate(initialNextInvoiceDate || "");
    setFile(null);
    setSaving(false);
    setError("");
  }, [show, initialBuilding, invoice?.id]); // wystarczy po id; opis wciągamy przy otwarciu

  const handleSetBuilding = (b) => {
    setBuilding(b || null);
  };

  const fileError = useMemo(() => {
    if (!file) return "";
    const isPdfByType = file.type === "application/pdf";
    const isPdfByName = (file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdfByType && !isPdfByName) return "Dozwolony jest tylko plik PDF.";
    return "";
  }, [file]);

  const validate = () => {
    if (!building?.id) return "Wybierz obiekt.";
    if (!description || description.trim() === "") return "Wpisz opis.";
    if (isRecurring && !nextInvoiceDate) return "Wybierz datę kolejnej faktury.";

    // create: plik wymagany
    if (!isEdit) {
      if (!file) return "Załącz plik PDF.";
      if (fileError) return fileError;
      return "";
    }

    // edit: plik opcjonalny (tylko jeśli chcesz nadpisać)
    if (file && fileError) return fileError;

    return "";
  };

  const handleClose = () => {
    if (saving) return;
    onHide?.();
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const formData = new FormData();

      if (isEdit) {
        formData.append("id", invoice.id);
      }

      formData.append("buildingId", building?.id);
      if (invoiceToIssueId) formData.append("invoiceToIssueId", invoiceToIssueId);
      if (isRecurring) {
        formData.append("recurringInvoiceId", recurringInvoiceId);
        formData.append("nextInvoiceDate", nextInvoiceDate);
      }
      formData.append("description", description);

      // edit: dołącz plik tylko jeśli user wybrał nowy
      if (file) {
        formData.append("file", file);
      }

      const result = isEdit
        ? await invoices.updateInvoice(formData)
        : await invoices.addInvoice(formData);

      if (!result?.success) {
        setError(handleResponseCode(result.status, result.error));
        setSaving(false);
        return;
      }

      onSaved?.(result.content, building, { nextInvoiceDate });
      onHide?.();
    } catch (e) {
      setError(e?.message || "Wystąpił błąd podczas zapisu.");
      setSaving(false);
    }
  };

  const serverFileName = invoice?.file_name || "";

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Wybierz obiekt</Form.Label>
            <SearchBuildingBar
              onSelect={handleSetBuilding}
              defaultValue={building}
              enabled={initialBuilding == null && !isEdit}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Opis</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Wpisz opis..."
              disabled={saving}
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              {isEdit ? "Plik (PDF) – opcjonalnie, aby nadpisać" : "Załącz plik (PDF)"}
            </Form.Label>

            <Form.Control
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={saving}
            />

            {/* info o pliku na serwerze w trybie edycji */}
            {isEdit && serverFileName && !file ? (
              <div className="mt-2 small text-muted">
                Aktualnie na serwerze: <strong>{serverFileName}</strong>
              </div>
            ) : null}

            {file ? (
              <div className="mt-2 small">
                Wybrano nowy plik: <strong>{file.name}</strong>
              </div>
            ) : null}

            {fileError ? <div className="mt-2 text-danger small">{fileError}</div> : null}
          </Form.Group>

          {isRecurring ? (
            <Form.Group className="mt-3 mb-2">
              <Form.Label>Data kolejnej faktury</Form.Label>
              <Form.Control
                type="date"
                value={nextInvoiceDate}
                onChange={(e) => setNextInvoiceDate(e.target.value)}
                disabled={saving}
              />
              <div className="mt-1 small text-muted">
                Domyślnie wyliczona z częstotliwości — możesz ją zmienić.
              </div>
            </Form.Group>
          ) : null}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Zapisywanie...
            </>
          ) : isEdit ? (
            "Zapisz zmiany"
          ) : (
            "Zapisz"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
